import { useMemo } from "react";

import { DataTable } from "@impero-com/common/components/dataTable";
import { FormSection } from "@impero-com/common/components/legacyWidgets";
import { t } from "@impero-com/common/utils/i18n";
import { FieldError } from "@impero-com/ui";
import {
    Control,
    RefCallBack,
    UseFormTrigger,
    useController,
    useWatch,
} from "react-hook-form";

import {
    ControlPolicies,
    NotificationType,
    NotificationRecipientKind,
    EntityView,
} from "api/generated/model";
import { mapPropsToTableDataSource } from "components/controlAssignment/dataSource";
import { EditAssignmentsTable } from "components/controlAssignment/editAssignmentsTable";
import { getAssignmentIdToSodIssues } from "components/controlAssignment/getAssignmentIdToSodIssues";
import {
    TableSectionHeader,
    buildColumns,
} from "components/controlAssignment/table";
import * as assignmentTheme from "components/controlAssignment/theme.css";
import { ControlAssignmentResources } from "components/controlAssignment/types";
import {
    ControlAssignmentData,
    controlAssignmentDataToControlAssignmentGroups,
} from "utils/controls/assignmentUtils";
import { Guid } from "utils/guid";

import { useFeatureFlag } from "../../../../utils/hooks/useFeatureFlag";
import { useEntityEditPermissions } from "../../hooks/useEntityEditPermissions";
import { ControlViewFormSchema } from "../formSchema";

import { ControlPolicyField } from "./ControlPolicyField";

type AssignmentGroupId = Guid;

export interface ControlAssignmentsSectionProps {
    control: Control<ControlViewFormSchema>;
    trigger: UseFormTrigger<ControlViewFormSchema>;
    hasFullAssignmentsAccess: boolean;
    linkedEntities: EntityView[];
    disabled?: boolean;
    resources: ControlAssignmentResources;
    controlPolicies: ControlPolicies;
    notificationRecipients: Record<
        AssignmentGroupId,
        Partial<Record<NotificationType, NotificationRecipientKind[]>>
    >;
}

export function ControlAssignmentsSection({
    control,
    trigger,
    disabled,
    hasFullAssignmentsAccess,
    linkedEntities,
    resources,
    controlPolicies,
    notificationRecipients,
}: ControlAssignmentsSectionProps) {
    const controlProgramId = useWatch({ control, name: "controlProgramId" });
    // Row data controller (assignments, tags, entities, notifications)
    const { field: assignmentField, fieldState } = useController({
        control,
        name: "controlAssignment",
    });
    // Individual controllers for the checkboxes
    const { field: loginField } = useController({
        control,
        name: "controlAssignment.loginProtectionEnabled",
    });
    const { field: sodField } = useController({
        control,
        name: "controlAssignment.segregationOfDutiesEnabled",
    });

    const value = assignmentField.value;
    const entityIds = value.entityIds;
    const { canEditAnyLinkedEntity, canEditEntityInRow } =
        useEntityEditPermissions({ entityIds, linkedEntities });

    const entitiesById = useMemo(() => {
        const map = new Map<number, EntityView>();
        linkedEntities.forEach((entity) => {
            map.set(entity.id, entity);
        });
        return map;
    }, [linkedEntities]);

    const isAssignmentSectionReadOnly =
        disabled && !canEditAnyLinkedEntity && !hasFullAssignmentsAccess;

    const isRowClickable = (assignmentGroupId: AssignmentGroupId): boolean =>
        hasFullAssignmentsAccess || canEditEntityInRow(assignmentGroupId);

    // Form mode is "onBlur" (see create.tsx / update.tsx), so checkbox changes
    // don't auto-validate. Trigger the parent path explicitly so SoD / login
    // protection errors surface immediately instead of only on submit.
    const handleLoginProtectionChange = (enabled: boolean) => {
        loginField.onChange(enabled);
        void trigger("controlAssignment");
    };
    const handleSegregationOfDutiesChange = (enabled: boolean) => {
        sodField.onChange(enabled);
        void trigger("controlAssignment");
    };
    // Revalidate when the assignment changes to ensure errors are updated immediately
    const handleAssignmentChange = (data: ControlAssignmentData) => {
        assignmentField.onChange(data);
        if (fieldState.error) {
            void trigger("controlAssignment");
        }
    };

    return isAssignmentSectionReadOnly ? (
        <ControlAssignmentsSectionRead
            value={value}
            loginProtectionEnabled={value.loginProtectionEnabled}
            controlPolicies={controlPolicies}
            segregationOfDutiesEnabled={value.segregationOfDutiesEnabled}
            resources={resources}
            notificationRecipients={notificationRecipients}
            entitiesById={entitiesById}
            disabled={disabled}
        />
    ) : (
        <ControlAssignmentsSectionEdit
            value={value}
            onChange={handleAssignmentChange}
            fieldRef={assignmentField.ref}
            error={fieldState.error?.message}
            loginProtectionEnabled={value.loginProtectionEnabled}
            controlPolicies={controlPolicies}
            onLoginProtectionChange={handleLoginProtectionChange}
            segregationOfDutiesEnabled={value.segregationOfDutiesEnabled}
            onSegregationOfDutiesChange={handleSegregationOfDutiesChange}
            resources={resources}
            disabled={disabled}
            notificationRecipients={notificationRecipients}
            controlProgramId={controlProgramId}
            isRowClickable={isRowClickable}
            entitiesById={entitiesById}
        />
    );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ControlAssignmentDataProps {
    value: ControlAssignmentData;
    onChange: (data: ControlAssignmentData) => void;
    fieldRef: RefCallBack;
    error?: string;
    loginProtectionEnabled: boolean;
    controlPolicies: ControlPolicies;
    onLoginProtectionChange: (enabled: boolean) => void;
    segregationOfDutiesEnabled: boolean;
    onSegregationOfDutiesChange: (enabled: boolean) => void;
    resources: ControlAssignmentResources;
    isAssignmentSectionReadOnly?: boolean;
    disabled?: boolean;
    notificationRecipients: Record<
        AssignmentGroupId,
        Partial<Record<NotificationType, NotificationRecipientKind[]>>
    >;
    controlProgramId: number | null;
    isRowClickable: (assignmentGroupId: AssignmentGroupId) => boolean;
    entitiesById: Map<number, EntityView>;
}

function ControlAssignmentsSectionEdit({
    value,
    onChange,
    fieldRef,
    error,
    loginProtectionEnabled,
    controlPolicies,
    onLoginProtectionChange,
    segregationOfDutiesEnabled,
    onSegregationOfDutiesChange,
    resources,
    disabled,
    notificationRecipients,
    controlProgramId,
    isRowClickable,
    entitiesById,
}: ControlAssignmentDataProps) {
    const assignmentIdToSodIssues = getAssignmentIdToSodIssues(value);

    const handleDeleteRow = (assignmentGroupId: Guid) => {
        const omitGroup = <V,>(
            record: Record<AssignmentGroupId, V>
        ): Record<AssignmentGroupId, V> =>
            Object.fromEntries(
                Object.entries(record).filter(
                    ([id]) => id !== assignmentGroupId
                )
            );

        onChange({
            ...value,
            initialAssignment: omitGroup(value.initialAssignment),
            subsequentAssignments: value.subsequentAssignments.map(omitGroup),
            tagIds: omitGroup(value.tagIds),
            entityIds: omitGroup(value.entityIds),
            notificationRecipients: omitGroup(value.notificationRecipients),
        });
    };

    return (
        <div ref={fieldRef} tabIndex={-1} className={assignmentTheme.table}>
            <TableSectionHeader />
            <EditAssignmentsTable
                value={value}
                onChange={onChange}
                resources={resources}
                assignmentIdToSodIssues={assignmentIdToSodIssues}
                onDeleteRow={handleDeleteRow}
                notificationRecipients={notificationRecipients}
                controlProgramId={controlProgramId}
                isRowClickable={isRowClickable}
                entitiesById={entitiesById}
            />
            <FieldError error={error} />
            <FormSection>
                <ControlPolicyFields
                    controlPolicies={controlPolicies}
                    loginProtectionEnabled={loginProtectionEnabled}
                    segregationOfDutiesEnabled={segregationOfDutiesEnabled}
                    onLoginProtectionChange={
                        disabled ? undefined : onLoginProtectionChange
                    }
                    onSegregationOfDutiesChange={
                        disabled ? undefined : onSegregationOfDutiesChange
                    }
                />
            </FormSection>
        </div>
    );
}

interface ControlPolicyFieldsProps {
    controlPolicies: ControlPolicies;
    loginProtectionEnabled: boolean;
    segregationOfDutiesEnabled: boolean;
    /** Without it, the login required field is read only. */
    onLoginProtectionChange?: (enabled: boolean) => void;
    /** Without it, the segregation of duties field is read only. */
    onSegregationOfDutiesChange?: (enabled: boolean) => void;
}

function ControlPolicyFields({
    controlPolicies,
    loginProtectionEnabled,
    segregationOfDutiesEnabled,
    onLoginProtectionChange,
    onSegregationOfDutiesChange,
}: ControlPolicyFieldsProps) {
    return (
        <div className={assignmentTheme.checkboxColumn}>
            <ControlPolicyField
                id="login-protection"
                label={t("CreateControl_LoginProtectionLabel")}
                checkboxLabel={t("Control_LoginProtection_Checkbox")}
                policy={controlPolicies.loginProtection}
                checked={loginProtectionEnabled}
                onChange={onLoginProtectionChange}
            />
            <ControlPolicyField
                id="segregation-of-duties"
                label={t("Control_SegregationOfDuties_Label")}
                checkboxLabel={t("Control_SegregationOfDuties_Checkbox")}
                info={t("Control_SegregationOfDuties_Tooltip")}
                policy={controlPolicies.segregationOfDuties}
                checked={segregationOfDutiesEnabled}
                onChange={onSegregationOfDutiesChange}
            />
        </div>
    );
}

type ControlAssignmentsSectionReadProps = Omit<
    ControlAssignmentDataProps,
    | "isRowClickable"
    | "controlProgramId"
    | "onChange"
    | "fieldRef"
    | "error"
    | "onLoginProtectionChange"
    | "onSegregationOfDutiesChange"
>;

function ControlAssignmentsSectionRead({
    value,
    loginProtectionEnabled,
    controlPolicies,
    segregationOfDutiesEnabled,
    resources,
    entitiesById,
}: ControlAssignmentsSectionReadProps) {
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const assignmentGroups =
        controlAssignmentDataToControlAssignmentGroups(value);

    const dataSource = useMemo(
        () =>
            mapPropsToTableDataSource(
                assignmentGroups,
                resources,
                controlEntitiesEnabled
            ),
        [assignmentGroups, resources, controlEntitiesEnabled]
    );

    if (!value) {
        return <div />;
    }

    return (
        <div className={assignmentTheme.table}>
            <TableSectionHeader />
            <DataTable
                id="assignmentData"
                columnBuilders={buildColumns(entitiesById)}
                dataView={dataSource}
            />
            <FormSection>
                <ControlPolicyFields
                    controlPolicies={controlPolicies}
                    loginProtectionEnabled={loginProtectionEnabled}
                    segregationOfDutiesEnabled={segregationOfDutiesEnabled}
                />
            </FormSection>
        </div>
    );
}
